import React from "react";
import {Button, Card, CardBody, CardHeader, Dropdown, GdCheckbox, Input, Loader, Toast} from "gd-react";
import GridDuck from "gridduck";
import {confirmAlert} from "react-confirm-alert";
import history from "../../meta/history";
import AddManualDeviceModal from "../../modals/AddDevice/AddManualDeviceModal";
// ZIGBEE_STATE_METHOD is announced only by firmware new enough to report its live state, so the section keys on it, not the model.
import gatewayCommandSocket, {gatewaySupportsMethod, ZIGBEE_STATE_METHOD} from "../../services/gatewayCommandSocket";

// A gateway reports its trust list as either bare IEEE address strings (the hub) or objects
// describing each device (the pi gateway, which knows the model from the interview). Both are
// flattened to the same shape so the list renders the same either way.
function normaliseTrustedItem(trustedItem) {
	if (trustedItem && typeof trustedItem === 'object') {
		return {
			code: trustedItem.id || trustedItem.ieeeAddr || '',
			modelID: trustedItem.modelID,
			manufacturerName: trustedItem.manufacturerName,
			interviewed: trustedItem.interviewed,
			lastSeen: trustedItem.lastSeen,
			reportableAttributes: trustedItem.reportableAttributes,
			// A string entry carries nothing beyond the address, so there is no detail line to
			// draw - as opposed to an object whose fields happen to be empty, which is telling.
			detailed: true
		};
	}
	return {code: trustedItem || '', detailed: false};
}

function formatLastSeen(lastSeen) {
	if (!lastSeen) return null;
	let seconds = Math.round((Date.now() - lastSeen) / 1000);
	if (seconds < 60) return 'last seen just now';
	if (seconds < 3600) return 'last seen ' + Math.round(seconds / 60) + 'm ago';
	if (seconds < 86400) return 'last seen ' + Math.round(seconds / 3600) + 'h ago';
	return 'last seen ' + Math.round(seconds / 86400) + 'd ago';
}

// The same convention AuditModal uses, backed by the PNGs in public/images/products. Only a
// device already added as an asset carries a sku, so anything else falls back to the placeholder.
function productImage(sku) {
	return '/images/products/' + (sku || 'unknown') + '.png';
}

// {genOnOff: ['onOff'], seMetering: ['currentSummDelivered']} -> a one line summary. An empty set
// is worth saying out loud: a device with nothing configured is one that will never report.
function describeReporting(reportableAttributes) {
	if (!reportableAttributes) return null;
	let clusters = Object.keys(reportableAttributes);
	if (clusters.length === 0) return 'No reporting configured';
	return 'Reporting ' + clusters
		.map(cluster => cluster + ' (' + (reportableAttributes[cluster] || []).join(', ') + ')')
		.join(', ');
}

// The two gateways spell the same address differently - the hub sends 0015BC002F009618 and the pi
// sends 0x0015bc002f009618 - so an exact match against asset.code would leave every pi device
// showing as unknown.
function sameAddress(a, b) {
	let strip = (value) => String(value || '').replace(/^0x/i, '').toLowerCase();
	return strip(a) !== '' && strip(a) === strip(b);
}

class GatewayZigbee extends React.Component {
	constructor(props) {
		super(props);
		let self = this;
		this.state = {
			updateRef: 0,
			// A pi-duck's list, as it answered the last refresh. Null until asked; the hub's
			// arrives on this.trustedList instead, pushed after its next check-in.
			commandTrustList: null,
			refreshError: null,
			// Hidden until the hub has said it can answer, so nothing flashes up and disappears.
			stateSupported: false,
			zigbeeState: null,
			zigbeeStateReadAt: null,
			zigbeeStateError: null,
			// The trust list entry being added as an asset, while the add-device modal is open.
			addingCode: null,
		};
		this.updatedExternally = this.updatedExternally.bind(this);
		this.refresh = this.refresh.bind(this);
		this.untrust = this.untrust.bind(this);
		this.loadZigbeeState = this.loadZigbeeState.bind(this);
		this.loadAssets = this.loadAssets.bind(this);
		this.closeAddDevice = this.closeAddDevice.bind(this);
	}

	// The site's assets, which the trust list is matched against by address. Kept off state, so a
	// finished load re-renders explicitly: that is what takes the Add button away once one is added.
	loadAssets() {
		let self = this;
		return GridDuck.getAssets({
			filters: [
				{
					field: 'siteId',
					value: this.props.item.siteId
				}
			],
			getAll: true
		}).then(function (assetList) {
			if (self.unmounted) return;
			self.assetList = assetList;
			self.setState({updateRef: self.state.updateRef + 1});
		});
	}

	closeAddDevice() {
		this.setState({addingCode: null});
		this.loadAssets();
	}

	untrust(assetCode) {
		let self = this;
		confirmAlert({
			customUI: ({onClose}) => {
				return (
					<div className='dialog'>
						<div className={"top-content"}>
							<h2>Untrust {assetCode}</h2>
							<p>Untrusting {assetCode} will result in the device resetting and switching off. Are you sure?</p>
						</div>
						<div className={"bottom-menu"}>
							<Button label={"No"} color={'gd-grey'} onClick={onClose}/>
							<Button color={'gd-red'}
									onClick={() => {
										GridDuck.createGatewayTrustedDeviceUntrustRequest({
											assetCode: assetCode,
											gatewayId: self.props.item.id
										}).then(function() {
											onClose();
										});
									}}
									label={"Yes, un-trust"}
							/>
						</div>
					</div>
				);
			}
		});
	}


	refresh() {
		let self = this;
		this.setState({refreshError: null});
		// The contract's model table decides, so a model that cannot serve the command keeps the old path.
		if (!gatewaySupportsMethod(this.props.item, 'zigbee.listDevices')) {
			return this.refreshByConfigFlag();
		}
		return this.refreshOverCommandChannel()
			.catch(function (err) {
				// Pi-duck firmware from before the command channel never announces it, but still
				// honours the flag on its next check-in - so it gets the old path, not an error.
				if (err.code === 'UNSUPPORTED') {
					return self.refreshByConfigFlag();
				}
				self.setState({refreshError: 'Could not read the trust list: ' + err.message});
				return Promise.reject(err);
			});
	}

	// The hub reports its list on its next check-in once the flag is set, and it arrives here
	// through the trust-list subscription rather than as this call's result.
	refreshByConfigFlag() {
		let self = this;
		self.trustedList = null;
		this.setState({commandTrustList: null});
		this.setState({
			updateRef: this.state.updateRef++
		});
		return this.props.item.set({
			getTrustedList: true
		}).then(function () {
			GridDuck.getGatewayTrustList({
				id: self.props.item.id
			}).then(function (trustedList) {
				self.trustedList = trustedList;
				self.trustedList.on('updated', self.updatedExternally);
			});
		});
	}

	// A pi-duck answers with the list itself, so there is no flag to set and no check-in to wait
	// for: the list is on screen when the button settles, rather than whenever the hub next polls.
	// It is the same getTrustDeviceList() the check-in path reports, so it renders identically.
	refreshOverCommandChannel() {
		let self = this;
		return gatewayCommandSocket.invoke(this.props.item.id, 'zigbee.listDevices')
			.then(function (result) {
				self.setState({commandTrustList: (result && result.devices) || []});
			});
	}

	// Asks the hub what it announced, which is answered from the server's cache of its hello and
	// costs no radio time. Anything short of a yes - a hub, an older pi-duck, or one that cannot be
	// reached - leaves the section hidden. It only decides whether the section shows: the state
	// itself is read when someone asks for it, not on the way into the page.
	checkStateSupport() {
		let self = this;
		let item = this.props.item;
		if (!gatewaySupportsMethod(item, ZIGBEE_STATE_METHOD)) return;
		gatewayCommandSocket.capabilities(item.id)
			.then(function (res) {
				if (self.unmounted || self.props.item.id !== item.id) return;
				self.setState({stateSupported: (res.methods || []).indexOf(ZIGBEE_STATE_METHOD) !== -1});
			})
			.catch(function () { /* unreachable is not a yes */ });
	}

	loadZigbeeState() {
		let self = this;
		let gatewayId = this.props.item.id;
		this.setState({zigbeeStateError: null});
		return gatewayCommandSocket.invoke(gatewayId, ZIGBEE_STATE_METHOD)
			.then(function (result) {
				if (self.unmounted || self.props.item.id !== gatewayId) return;
				self.setState({zigbeeState: result, zigbeeStateReadAt: new Date()});
			})
			.catch(function (err) {
				if (!self.unmounted) self.setState({zigbeeStateError: 'Could not read the zigbee state: ' + err.message});
				return Promise.reject(err);
			});
	}

	componentDidMount() {
		this.loadAssets();
		this.checkStateSupport();
	}

	updatedExternally(field) {
		let self = this;
		this.setState({
			updateRef: this.state.updateRef++,
		});
	}

	componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS) {
		let self = this;
		if (prevProps !== this.props || this.props.item.id !== prevProps.item.id) {
			if (prevProps.item) {
				prevProps.item.off('updated', this.updatedExternally);
			}
			this.props.item.on('updated', this.updatedExternally);
			this.updatedExternally();
		}
		// Only on a different gateway: the check above also fires on every parent re-render, and
		// asking the hub again each time would be a query per render.
		if (prevProps.item && prevProps.item.id !== this.props.item.id) {
			this.setState({stateSupported: false, zigbeeState: null, zigbeeStateReadAt: null, zigbeeStateError: null});
			this.checkStateSupport();
		}
	}

	componentWillUnmount() {
		// The state checks start on mount unasked, so a quick tab switch can outlive them.
		this.unmounted = true;
		if (this.props.item && this.props.item.off) {
			this.props.item.off('updated', this.updatedExternally);
		}
		if (this.trustedList) {
			this.trustedList.off('updated', this.updatedExternally)
		}
	}

	render() {
		let self = this;
		let trustItems = [];
		// Whichever path answered: only a command sets commandTrustList, and falling back to the
		// flag clears it, so a hub or an older pi-duck always renders the pushed list.
		let trustList = this.state.commandTrustList
			|| (this.trustedList && this.trustedList.trustList);
		if (trustList) {
			trustList.forEach(function (trustedItem) {
				let device = normaliseTrustedItem(trustedItem);
				if (!device.code) return;
				let thisAsset = self.assetList
					? self.assetList.list.find(a => sameAddress(a.code, device.code))
					: null;

				let describedAs = [device.manufacturerName, device.modelID].filter(Boolean).join(' ');
				let name;
				if (thisAsset) {
					name = thisAsset.name;
				} else if (describedAs) {
					// Not an asset here yet, but the gateway interviewed it and knows what it is.
					name = describedAs;
				} else {
					name = 'Unknown Appliance';
				}

				// Only an object entry has anything to say here; a bare address has not.
				let details = [];
				if (device.detailed) {
					// Skipped when the model is already doing duty as the name above.
					if (describedAs && name !== describedAs) details.push(describedAs);
					if (device.interviewed === false) details.push('interview incomplete');
					let lastSeen = formatLastSeen(device.lastSeen);
					if (lastSeen) details.push(lastSeen);
				}
				let reporting = device.detailed ? describeReporting(device.reportableAttributes) : null;

				trustItems.push(
					<div key={device.code} style={{'display':'flex','alignItems':'flex-start','marginBottom':'20px'}}>
						<div style={{
							'width':'40px','height':'40px','marginRight':'15px','flexShrink':'0',
							'backgroundImage':'url(' + productImage(thisAsset ? thisAsset.sku : null) + ')',
							'backgroundSize':'contain','backgroundRepeat':'no-repeat','backgroundPosition':'center'
						}}/>
						<div style={{'flex':'1','marginRight':'20px'}}>
							<p style={{'margin':'0 0 4px 0'}}>{name} ({device.code})</p>
							{details.length > 0 ?
								<p style={{'margin':'0','fontSize':'12px','color':'#767676'}}>{details.join(' · ')}</p> : null}
							{reporting ?
								<p style={{'margin':'4px 0 0 0','fontSize':'12px','color':'#767676'}}>{reporting}</p> : null}
						</div>
						{/* Only once the site's assets are known, so a device that is an asset never flashes one. */}
						{!thisAsset && self.assetList && self.props.item._permission === 'admin' ?
							<div style={{'marginRight':'8px'}}>
								<Button onClick={() => self.setState({addingCode: device.code})} additionalclasses={'sm'}
										color={'gd-green'} label={'Add'}/>
							</div> : null}
						<Button onClick={() => self.untrust(device.code)} additionalclasses={'sm'} color={'gd-red'}
								label={'Un-Trust'}/>
					</div>
				)
			})
		}
		let item = this.props.item;
		return (
			<div className={"page grey narrow not-flex"}>
				{this.state.addingCode ?
					<AddManualDeviceModal
						open={true}
						onClose={this.closeAddDevice}
						prefill={{
							site: {id: item.siteId, title: item.siteName, icon: 'FaBuilding'},
							hub: {id: item.id, title: item.name, subtitle: item.locationName || '', icon: 'FaSquare'},
							code: this.state.addingCode,
						}}/> : null}
				<div className={'column top'}>
					<div className={'detail-content'}>
						<Card>
							<CardHeader title={'Zigbee Trust List'}/>
							<CardBody>
								{trustItems}
							</CardBody>
						</Card>
					</div>
					<div className={'detail-content'}>
						<Card>
							<CardBody>
								<Button onClick={self.refresh} progressRes additionalclasses={'sm'} color={'gd-blue'}
										label={'Refresh'}/>
								{this.state.refreshError ?
									<p style={{fontSize: '11px', margin: '8px 0 0'}}>{this.state.refreshError}</p> : null}
							</CardBody>
						</Card>
					</div>
					{this.state.stateSupported ?
						<div className={'detail-content'}>
							<Card>
								<CardHeader title={'Zigbee State'}/>
								<CardBody>
									<p>
										The coordinator, the join window, and the state each paired device has
										reported, as the hub holds it in memory.
									</p>
									<br/>
									<Button onClick={self.loadZigbeeState} progressRes additionalclasses={'sm'}
											color={'gd-blue'}
											label={this.state.zigbeeState ? 'Refresh' : 'Read State'}/>
									{this.state.zigbeeStateError ?
										<p style={{fontSize: '11px', margin: '8px 0 0'}}>{this.state.zigbeeStateError}</p> : null}
									{this.state.zigbeeState ?
										<div>
											<p style={{fontSize: '11px', margin: '12px 0 6px', color: '#767676'}}>
												Read at {this.state.zigbeeStateReadAt.toLocaleTimeString()}
											</p>
											<pre style={{
												margin: 0, maxHeight: '600px', overflow: 'auto', fontSize: '12px',
												background: '#f7f7f7', padding: '12px', borderRadius: '4px'
											}}>{JSON.stringify(this.state.zigbeeState, null, 2)}</pre>
										</div> : null}
								</CardBody>
							</Card>
						</div> : null}
				</div>
			</div>
		)
	}

}

export default GatewayZigbee;
